/**
 * ReservoirComplianceGatewayPage.tsx — Reservoir-aeration-specific compliance gate
 *
 * Tracks the full UK regulatory set for a reservoir aeration scheme:
 *   - Reservoirs Act 1975 (panel-engineer involvement)
 *   - DWI raw-water continuity
 *   - Environment Agency consent
 *   - CDM 2015 PD / PC duties
 *   - DSEAR (blower compound)
 *   - Noise consent / acoustic baseline
 *   - South East Water internal sign-off
 *
 * Each item is editable, persisted, and a PASS/CHECK badge rolls up to the
 * top-line gateway readiness score.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ShieldCheck, ArrowLeft, CheckCircle2, AlertTriangle, Clock,
  FileText, Save, Printer,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { toast } from 'sonner';

const KEY = 'rb_reservoir_compliance_v1';

interface CheckItem {
  id: string;
  category: string;
  reference: string;
  title: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'submitted' | 'approved' | 'na';
  owner: string;
  dueDate: string;
  notes: string;
}

const DEFAULT_CHECKS: CheckItem[] = [
  // Reservoirs Act 1975
  {
    id: 'res-1', category: 'Reservoirs Act 1975',
    reference: 'Section 10 / 12', title: 'All-Reservoirs Panel Engineer involvement',
    description: 'Confirm AR Panel Engineer reviews any works affecting the impounding structure.',
    status: 'not_started', owner: 'Project manager', dueDate: '', notes: '',
  },
  {
    id: 'res-2', category: 'Reservoirs Act 1975',
    reference: 'Schedule 2', title: 'Construction Engineer appointment if required',
    description: 'Determine whether installation works require a Construction Engineer under the Act.',
    status: 'not_started', owner: 'Project manager', dueDate: '', notes: '',
  },

  // DWI
  {
    id: 'dwi-1', category: 'DWI',
    reference: 'WSR Reg 26', title: 'Raw water continuity statement',
    description: 'Demonstrate aeration scheme does not introduce contamination or interrupt raw-water quality.',
    status: 'not_started', owner: 'Water quality lead', dueDate: '', notes: '',
  },
  {
    id: 'dwi-2', category: 'DWI',
    reference: 'WSR Reg 31', title: 'Materials & substances approval',
    description: 'Verify all wetted materials (membranes, ballast, mooring) are WRAS / Reg 31 approved.',
    status: 'not_started', owner: 'Procurement', dueDate: '', notes: '',
  },

  // Environment Agency
  {
    id: 'ea-1', category: 'Environment Agency',
    reference: 'Water Resources Act 1991', title: 'Discharge consent (if any)',
    description: 'Confirm whether any discharge from blower compound (condensate, washout) requires a consent.',
    status: 'not_started', owner: 'Environmental lead', dueDate: '', notes: '',
  },
  {
    id: 'ea-2', category: 'Environment Agency',
    reference: 'WFD Article 4(7)', title: 'WFD non-deterioration check',
    description: 'Confirm scheme does not cause deterioration in any WFD water-body classification element.',
    status: 'not_started', owner: 'Environmental lead', dueDate: '', notes: '',
  },
  {
    id: 'ea-3', category: 'Environment Agency',
    reference: 'INNS Order', title: 'Biosecurity / Check-Clean-Dry protocol',
    description: 'Plant, divers, dive kit and pilot modules cleaned to INNS standard before submersion.',
    status: 'not_started', owner: 'Site supervisor', dueDate: '', notes: '',
  },

  // CDM 2015
  {
    id: 'cdm-1', category: 'CDM 2015',
    reference: 'Reg 5', title: 'Principal Designer appointment',
    description: 'PD appointed in writing, F10 notification submitted.',
    status: 'not_started', owner: 'Client / Atkins', dueDate: '', notes: '',
  },
  {
    id: 'cdm-2', category: 'CDM 2015',
    reference: 'Reg 12', title: 'Pre-construction information',
    description: 'PCI pack issued to all designers and contractors.',
    status: 'not_started', owner: 'Principal Designer', dueDate: '', notes: '',
  },
  {
    id: 'cdm-3', category: 'CDM 2015',
    reference: 'Reg 12', title: 'Construction phase plan',
    description: 'CPP issued by Principal Contractor before any works on site.',
    status: 'not_started', owner: 'Principal Contractor', dueDate: '', notes: '',
  },
  {
    id: 'cdm-4', category: 'CDM 2015',
    reference: 'Reg 12', title: 'Health & safety file at handover',
    description: 'H&S file compiled and delivered to client at project close.',
    status: 'not_started', owner: 'Principal Designer', dueDate: '', notes: '',
  },

  // DSEAR
  {
    id: 'ds-1', category: 'DSEAR',
    reference: 'DSEAR 2002', title: 'Hazardous area classification for blower compound',
    description: 'Zone classification (or non-classified) for the blower plant room.',
    status: 'not_started', owner: 'Safety lead', dueDate: '', notes: '',
  },
  {
    id: 'ds-2', category: 'DSEAR',
    reference: 'DSEAR 2002', title: 'Oil-free vs oil-injected blower decision',
    description: 'Document rationale for blower oil-management approach against DSEAR risk.',
    status: 'not_started', owner: 'Safety lead', dueDate: '', notes: '',
  },

  // Noise
  {
    id: 'no-1', category: 'Noise',
    reference: 'BS 4142:2014', title: 'Acoustic baseline survey',
    description: 'Baseline LA90 and LAeq at nearest noise-sensitive receptors.',
    status: 'not_started', owner: 'Acoustic consultant', dueDate: '', notes: '',
  },
  {
    id: 'no-2', category: 'Noise',
    reference: 'BS 4142:2014', title: 'Blower compound noise prediction',
    description: 'Predicted contribution at nearest receptor vs BS 4142 rating.',
    status: 'not_started', owner: 'Acoustic consultant', dueDate: '', notes: '',
  },

  // South East Water internal
  {
    id: 'sew-1', category: 'South East Water',
    reference: 'Internal', title: 'Operations sign-off',
    description: 'SEW operations team review of layout, access and SCADA integration.',
    status: 'not_started', owner: 'SEW Ops', dueDate: '', notes: '',
  },
  {
    id: 'sew-2', category: 'South East Water',
    reference: 'Internal', title: 'Asset management sign-off',
    description: 'SEW asset management approval of CAPEX / OPEX submission.',
    status: 'not_started', owner: 'SEW AM', dueDate: '', notes: '',
  },
  {
    id: 'sew-3', category: 'South East Water',
    reference: 'Internal', title: 'Water quality team sign-off',
    description: 'SEW water-quality team approval of pilot trial results and α-factor calibration.',
    status: 'not_started', owner: 'SEW WQ', dueDate: '', notes: '',
  },

  // Reservoirs Act 2 – flood / dam safety
  {
    id: 'res-3', category: 'Reservoirs Act 1975',
    reference: 'Section 8', title: 'Supervising engineer continuity',
    description: 'Confirm Supervising Engineer involvement maintained throughout works.',
    status: 'not_started', owner: 'Project manager', dueDate: '', notes: '',
  },
];

const STATUS_META: Record<CheckItem['status'], { label: string; colour: string; icon: typeof CheckCircle2 }> = {
  not_started: { label: 'Not started', colour: 'border-slate-700 bg-slate-900 text-slate-400', icon: Clock },
  in_progress: { label: 'In progress', colour: 'border-amber-700 bg-amber-900/20 text-amber-300', icon: Clock },
  submitted:   { label: 'Submitted',   colour: 'border-blue-700 bg-blue-900/20 text-blue-300', icon: FileText },
  approved:    { label: 'Approved',    colour: 'border-emerald-700 bg-emerald-900/20 text-emerald-300', icon: CheckCircle2 },
  na:          { label: 'N/A',         colour: 'border-slate-700 bg-slate-800 text-slate-500', icon: AlertTriangle },
};

export default function ReservoirComplianceGatewayPage() {
  const navigate = useNavigate();
  const [checks, setChecks] = useState<CheckItem[]>(() => {
    const persisted = loadPersisted<{ checks?: CheckItem[] }>(KEY, { checks: DEFAULT_CHECKS });
    return persisted.checks && persisted.checks.length ? persisted.checks : DEFAULT_CHECKS;
  });
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => { savePersisted(KEY, { checks }); }, [checks]);

  const categories = useMemo(() =>
    Array.from(new Set(checks.map(c => c.category))).sort(),
    [checks]
  );

  const filtered = useMemo(
    () => filter === 'all' ? checks : checks.filter(c => c.category === filter),
    [checks, filter]
  );

  const summary = useMemo(() => {
    const total = checks.length;
    const approved = checks.filter(c => c.status === 'approved' || c.status === 'na').length;
    const inProgress = checks.filter(c => c.status === 'in_progress' || c.status === 'submitted').length;
    const notStarted = checks.filter(c => c.status === 'not_started').length;
    const pct = Math.round((approved / total) * 100);
    return { total, approved, inProgress, notStarted, pct };
  }, [checks]);

  const update = (id: string, patch: Partial<CheckItem>) =>
    setChecks(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c));

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={ShieldCheck}
        title="Reservoir Aeration — Compliance Gateway"
        subtitle="Reservoirs Act, DWI, EA, CDM 2015, DSEAR, noise and client-internal sign-off tracking"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => { savePersisted(KEY, { checks }); toast.success('Saved'); }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Gateway readiness</div>
          <div className="text-3xl font-bold text-rb-brand mt-1">{summary.pct}%</div>
          <div className="text-xs text-slate-500 mt-1">{summary.approved} of {summary.total} items cleared</div>
        </div>
        <div className="rounded-xl border border-emerald-700 bg-emerald-900/20 p-4">
          <div className="text-xs text-emerald-300 uppercase tracking-wider">Approved / N&#8203;/A</div>
          <div className="text-3xl font-bold text-emerald-200 mt-1">{summary.approved}</div>
        </div>
        <div className="rounded-xl border border-amber-700 bg-amber-900/20 p-4">
          <div className="text-xs text-amber-300 uppercase tracking-wider">In progress</div>
          <div className="text-3xl font-bold text-amber-200 mt-1">{summary.inProgress}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Not started</div>
          <div className="text-3xl font-bold text-slate-300 mt-1">{summary.notStarted}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            filter === 'all' ? 'bg-rb-brand text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}>
          All ({checks.length})
        </button>
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              filter === cat ? 'bg-rb-brand text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}>
            {cat} ({checks.filter(c => c.category === cat).length})
          </button>
        ))}
      </div>

      {/* Items */}
      <div className="space-y-2">
        {filtered.map(item => {
          const meta = STATUS_META[item.status];
          const Icon = meta.icon;
          return (
            <div key={item.id} className={`rounded-xl border p-4 ${meta.colour}`}>
              <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{item.category}</span>
                    <span className="text-[10px] text-slate-500 font-mono">{item.reference}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-100 mt-0.5">{item.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{item.description}</div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">
                    <select value={item.status}
                      onChange={e => update(item.id, { status: e.target.value as CheckItem['status'] })}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100">
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <option key={k} value={k}>{m.label}</option>
                      ))}
                    </select>
                    <input value={item.owner} placeholder="Owner"
                      onChange={e => update(item.id, { owner: e.target.value })}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100" />
                    <input type="date" value={item.dueDate}
                      onChange={e => update(item.id, { dueDate: e.target.value })}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100" />
                    <input value={item.notes} placeholder="Notes"
                      onChange={e => update(item.id, { notes: e.target.value })}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
